/**
 * Map Context
 * RoofMapper v3 - 2D/3D Map State Management
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  MapMode,
  MapContextType,
  CameraState,
  MarkerPosition,
} from '../types/map';

const MapContext = createContext<MapContextType | undefined>(undefined);

interface MapProviderProps {
  children: ReactNode;
}

const defaultCameraState: CameraState = {
  center: {
    lat: 19.422521674084017,  // Sierra Madre
    lng: -99.21509913652666,  // Sierra Madre
    altitude: 60,
  },
  zoom: 19,
  heading: 0,
  tilt: 0, // 0 = bird's eye view (90° top-down)
  range: 500, // Start zoomed out more for faster loading
};

export const MapProvider: React.FC<MapProviderProps> = ({ children }) => {
  // Map mode (2D or 3D) - Start with 3D
  const [mapMode, setMapMode] = useState<MapMode>('3D');

  // Pin position
  const [pinPosition, setPinPosition] = useState<MarkerPosition | null>(null);

  // Camera state
  const [cameraState, setCameraState] = useState<CameraState>(defaultCameraState);

  // Map references
  const [map2DRef, setMap2DRef] = useState<google.maps.Map | null>(null);
  const [map3DRef, setMap3DRef] = useState<google.maps.maps3d.Map3DElement | null>(null);

  // UI state
  const [isMapLoading, setIsMapLoading] = useState<boolean>(false);
  const [mapError, setMapError] = useState<string | null>(null);

  const toggleMapMode = useCallback(() => {
    setMapMode((prev) => {
      // Simple toggle: 2D/STREET ↔ 3D
      if (prev === '3D') return '2D';
      if (prev === 'STREET') return '3D'; // From Street View, go to 3D
      return '3D'; // From 2D, go to 3D
    });
  }, []);

  const resetMap = useCallback(() => {
    setPinPosition(null);
    setCameraState(defaultCameraState);
    setMapError(null);
  }, []);

  const value: MapContextType = {
    // Map mode
    mapMode,
    setMapMode,
    toggleMapMode,

    // Position
    pinPosition,
    setPinPosition,

    // Camera
    cameraState,
    setCameraState,

    // Map refs
    map2DRef,
    map3DRef,
    setMap2DRef,
    setMap3DRef,

    // UI State
    isMapLoading,
    setIsMapLoading,
    mapError,
    setMapError,

    // Actions
    resetMap,
  };

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
};

// Custom hook to use map context
export const useMap = (): MapContextType => {
  const context = useContext(MapContext);
  if (context === undefined) {
    throw new Error('useMap must be used within a MapProvider');
  }
  return context;
};

export default MapContext;
